import * as React from "react";
import { omit } from "ramda";
import { Selection, type IObjectWithKey } from "@fluentui/react";
import { action, computed, makeObservable } from "mobx";
import { createUuid, type UUID } from "../../utils/uuid";
import type { DepartmentSortKey, Department } from "../../api";
import { observer } from "mobx-react";
import type { SortState } from "../../utils/sort-state";
import type { PaginationState } from "../../utils/pagination-state";
import { LayoutList } from "../layouts/layout-list";
import { ElofleetList, type ElofleetListProps } from "../atoms/elofleet-list";
import type { SelectionState } from "../../utils/selection-state";
import {
    type DepartmentsApiResource,
    type DepartmentsQuery,
} from "../../domain/repositories/repository-departments";
import { defaultPageSize } from "../../utils/constants";

export interface ListDepartmentsProps extends Omit<ElofleetListProps, "items" | "columns"> {
    readonly ids?: UUID[];
    readonly commandBar?: JSX.Element;
    readonly listStateId?: UUID;
}

interface ListDepartmentItem extends IObjectWithKey {
    readonly label: string;
    readonly createdAt: string;
    readonly key: UUID;
}

import { store } from "../../store";

@observer
export class ListDepartments extends React.Component<ListDepartmentsProps> {
    private _listStateId = createUuid();
    private selection = new Selection({
        onSelectionChanged: () => this.updateSelection(),
    });

    constructor(props: ListDepartmentsProps) {
        super(props);
        makeObservable(this);
    }

    componentDidMount(): void {
        store.serviceListStates.departments.initializeList(this.listStateId, {
            query: () => this.query,
            // Ignore this the url history for this list, as this is always shown in conjunction with other lists.
            ignoreUrl: true,
        });
    }
    @computed private get listStateId(): UUID {
        return this.props.listStateId ?? this._listStateId;
    }

    @computed private get sortState(): SortState<DepartmentSortKey> {
        return store.serviceListStates.departments.getSortState(this.listStateId);
    }

    @computed private get paginationState(): PaginationState<DepartmentsApiResource> {
        return store.serviceListStates.departments.getPaginationState(this.listStateId);
    }

    @computed private get selectionState(): SelectionState {
        return store.serviceListStates.departments.getSelectionState(this.listStateId);
    }

    @computed private get query(): DepartmentsQuery {
        return { pageSize: defaultPageSize, ...this.sortState.query };
    }

    @computed private get departments(): (Department | undefined)[] {
        if (this.props.ids) {
            return this.props.ids.map((id) => store.repositoryDepartments.byId(id));
        }
        return store.repositoryDepartments.byQuery(this.query, this.paginationState.pagination);
    }

    @computed private get items(): (ListDepartmentItem | undefined)[] {
        return this.departments.map((department) => {
            if (!department) {
                return;
            }
            return {
                label: department.label,
                createdAt: store.i18n.formatDateOnly(department.createdAt),
                key: department.id,
            };
        });
    }

    @action.bound private updateSelection(): void {
        // Update the listState's selectionState to the new selected keys.
        this.selectionState.updateSelection(
            this.selection.getSelection().map((item) => item.key as UUID),
        );
    }

    public render(): JSX.Element {
        if (!store.serviceListStates.departments.isInitialized(this.listStateId)) {
            return <></>;
        }
        // Forward properties that are used by Fluent UI to the `ShimmeredDetailsList`.
        const props = omit(["ids"], this.props);
        return (
            <LayoutList paginationState={this.paginationState} commandBar={this.props.commandBar}>
                <ElofleetList
                    {...props}
                    selection={this.selection}
                    onColumnHeaderClick={this.sortState.toggleColumn}
                    columns={this.sortState.patchColumns([
                        {
                            fieldName: "label",
                            name: store.i18n.t("listDepartments.column.label.name"),
                            key: "label",
                            minWidth: 100,
                        },
                        {
                            fieldName: "createdAt",
                            name: store.i18n.t("listDepartments.column.createdAt.name"),
                            key: "createdAt",
                            minWidth: 100,
                        },
                    ])}
                    items={this.items}
                />
            </LayoutList>
        );
    }
}
